// `CellEditor` (design-system spec §3.13, P3-E2 PR E2-2d, D-326) — one field
// edited in place, in a DataTable cell whose column passes `editable` (§3.3).
//
// The cell shows its value and a small edit button beside it, and the button
// is the only way in, so a row click keeps its meaning. Four kinds, each the
// shape of a single-field write that already exists:
//
//   select  — a list; Enter or a press on an option commits it.
//   user    — the same list behind a filter box, because a member list is
//             long and a reader looks a person up by name.
//   tags    — a checklist that stages its toggles and sends nothing while it
//             is open. Enter, or the apply button, sends one attach or detach
//             per tag whose state changed.
//   toggle  — no list: the press is the commit.
//
// Escape, a press outside, focus leaving the list, a scroll or the browser
// going offline reverts and sends nothing. A commit shows the new value at once and marks the cell busy
// until the write answers. The caller reads its own route's answer into a
// `CellWriteOutcome`: `committed`, `settled` (the server already held the
// value sent, as ASSIGNMENT_EXISTS says on the Owner cell, so the value stays
// and nothing is marked), `changed` (a refusal naming the value the server
// holds now, as OWNER_CHANGED and OPPORTUNITY_STAGE_CHANGED do, which the cell
// then shows) or `refused` (any other refusal, which puts the old value back).
// A refusal stays in the cell as a danger mark in the edit button's own place,
// with the caller's words as its description and title, until the next commit
// in that cell or a reload. The toast that also names it is never the sole
// record (§3.8). Every answer calls the caller's `onSettled`, which
// invalidates the row's query so the refetch confirms what the cell shows.
//
// The primitive holds no string and no route: every word, the options, the
// writability and the one function that sends the write come from the caller.
//
// RESERVED SIZE (§5). The value and a fixed 24 px slot are the cell's whole
// box in every state. The edit button, the busy mark and the danger mark take
// turns in that slot, a read-only cell keeps it empty, and the list floats
// fixed to the viewport, so opening, pending, rollback and a row turning
// read-only after its commit (a task's Done) move nothing around the cell.
// That row's cell takes the focus its unmounted button held.

import { onlineManager } from "@tanstack/react-query";
import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties, KeyboardEvent, ReactNode, RefObject } from "react";
import { Button } from "./button";
import { toast } from "./toast";

/** One entry of a list or checklist, labelled by the caller. */
export interface CellOption {
  readonly value: string;
  readonly label: string;
}

/** A write's answer that names no value: what a `tags` toggle can end in. */
export type CellWriteAnswer =
  | { readonly status: "committed" }
  | { readonly status: "settled" }
  | { readonly status: "refused"; readonly code: string; readonly message: string };

/** A write's answer as the caller reads its own route. `message` is the
 * caller's words for the refusal: the danger mark's description and the
 * toast's sentence. */
export type CellWriteOutcome<V> =
  | CellWriteAnswer
  | {
      readonly status: "changed";
      readonly current: V;
      readonly code: string;
      readonly message: string;
    };

/** One refused toggle of a mixed `tags` commit. `code` is undefined when the
 * write got no answer at all. */
export interface CellTagRefusal {
  readonly option: CellOption;
  readonly attach: boolean;
  readonly code: string | undefined;
  readonly message: string;
}

interface CellEditorBase<Row> {
  /** May this row's cell be written? A cell that may not shows its value with
   * `reason` as its accessible description and no edit button. */
  writable: (row: Row) => boolean;
  reason?: ((row: Row) => string | undefined) | undefined;
  /** The edit button's accessible name ("Change the owner of Acme"). */
  editLabel: (row: Row) => string;
  /** The busy mark's description while the write is in flight ("Saving"). */
  busyLabel: string;
  /** The refusal's words when the write got no answer (a dropped network). */
  failedLabel: string;
  /** When given, an accepted commit toasts this sentence. */
  committedLabel?: ((row: Row) => string) | undefined;
  /** Called once per commit after every answer, accepted or refused: the
   * caller invalidates the row's query here. */
  onSettled: (row: Row) => void;
  /** `data-testid` of the cell. Defaults to `ds-cell-editor`; the edit button
   * takes `<testId>-edit` and the list `<testId>-list`. */
  testId?: string | undefined;
}

interface ChoiceFields<Row> {
  value: (row: Row) => string | null;
  options: (row: Row) => readonly CellOption[];
  display: (value: string | null, row: Row) => ReactNode;
  /** The list's accessible name. */
  listLabel: (row: Row) => string;
  /** Sends the one write: the chosen value, and the value the cell showed,
   * which a route that refuses a stale write compares (`expected_owner_user_id`,
   * `from_stage_id`). */
  send: (row: Row, next: string, shown: string | null) => Promise<CellWriteOutcome<string | null>>;
  /** A choice the caller takes elsewhere instead of sending, as the Stage
   * cell's Won and Lost open the deal's own dialogs (D-326 ruling 4). True
   * closes the list and sends nothing, and the cell keeps its value. */
  divert?: ((row: Row, next: string) => boolean) | undefined;
}

export interface SelectCellEditor<Row> extends CellEditorBase<Row>, ChoiceFields<Row> {
  kind: "select";
}

export interface UserCellEditor<Row> extends CellEditorBase<Row>, ChoiceFields<Row> {
  kind: "user";
  /** The filter box's accessible name. */
  filterLabel: string;
  /** Said in the list when the filter matches no one. */
  noMatchLabel: string;
}

export interface TagsCellEditor<Row> extends CellEditorBase<Row> {
  kind: "tags";
  value: (row: Row) => readonly string[];
  /** The checklist's entries, given the set the cell shows, so an entry
   * offered only for clearing (an archived tag) leaves the checklist once the
   * cell no longer carries it, before the refetch lands. */
  options: (row: Row, shown: readonly string[]) => readonly CellOption[];
  display: (value: readonly string[], row: Row) => ReactNode;
  listLabel: (row: Row) => string;
  /** The checklist's apply button, which commits as Enter does. */
  applyLabel: string;
  /** Sends one attach or detach. Called once per tag whose state changed. */
  send: (
    row: Row,
    change: { readonly tag: string; readonly attach: boolean },
  ) => Promise<CellWriteAnswer>;
  /** The danger mark's description after a commit with refused toggles: it
   * names each refused tag with its code. */
  refusedLabel: (refused: readonly CellTagRefusal[], row: Row) => string;
}

export interface ToggleCellEditor<Row> extends CellEditorBase<Row> {
  kind: "toggle";
  value: (row: Row) => boolean;
  display: (value: boolean, row: Row) => ReactNode;
  send: (row: Row, next: boolean) => Promise<CellWriteOutcome<boolean>>;
}

/** A column's `editable` (design-system §3.3 and §3.13). */
export type CellEditable<Row> =
  SelectCellEditor<Row> | UserCellEditor<Row> | TagsCellEditor<Row> | ToggleCellEditor<Row>;

/** The cell's value alone, as a phone card shows it below the collapse width,
 * where no editor renders. */
export function cellEditableValue<Row>(editor: CellEditable<Row>, row: Row): ReactNode {
  switch (editor.kind) {
    case "select":
    case "user":
      return editor.display(editor.value(row), row);
    case "tags":
      return editor.display(editor.value(row), row);
    case "toggle":
      return editor.display(editor.value(row), row);
  }
}

// The list's box, restated from `.ds-ced-pop` in styles/office.css: it opens
// above the cell when the room below is less than its tallest and there is
// more room above, and it never runs off the right edge.
const POP_GAP = 4;
const POP_MAX_HEIGHT = 280;
const POP_MAX_WIDTH = 280;
/** PageUp and PageDown move the active option by about the list's height in
 * 28 px rows. */
const PAGE_STEP = 8;

interface Placement {
  readonly x: number;
  readonly y: number;
  readonly up: boolean;
}

/** Measured in the press that opens the list and never in an effect, so the
 * first render of the open list is its final one (§5). */
function placeBeside(anchor: HTMLElement): Placement {
  const box = anchor.getBoundingClientRect();
  const below = window.innerHeight - box.bottom;
  const up = below < POP_MAX_HEIGHT + POP_GAP && box.top > below;
  const x = Math.max(POP_GAP, Math.min(box.left, window.innerWidth - POP_MAX_WIDTH - POP_GAP));
  return { x, y: up ? window.innerHeight - box.top + POP_GAP : box.bottom + POP_GAP, up };
}

function placementStyle(at: Placement): CSSProperties {
  return {
    "--ds-ced-x": `${String(Math.round(at.x))}px`,
    "--ds-ced-y": `${String(Math.round(at.y))}px`,
  } as CSSProperties;
}

function subscribeOnline(onChange: () => void): () => void {
  return onlineManager.subscribe(onChange);
}

/** react-query's own network flag, the one every office read pauses on, so an
 * editor is disabled exactly while the reads are held. */
function useOffline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => !onlineManager.isOnline(),
    () => false,
  );
}

interface Held<V> {
  /** What the cell shows: the optimistic value, then the committed one. */
  readonly value: V;
  /** The row's value when the commit began, and then when it settled. The
   * held value gives way once a refetch brings the row anything else. */
  readonly base: V;
}

interface Settled<V> {
  readonly value: V;
  readonly refusal?: string | undefined;
}

/**
 * The write half every kind shares: the value the cell shows, the busy mark,
 * the refusal kept in the cell, and `commit`, which shows the optimistic value
 * at once, awaits the caller's write and settles the cell from its answer.
 */
function useCellWrite<Row, V>(
  row: Row,
  editor: CellEditorBase<Row>,
  server: V,
  same: (a: V, b: V) => boolean,
) {
  const [held, setHeld] = useState<Held<V> | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  // The row's value as the latest render saw it, which a commit reads when it
  // settles: a refetch that landed while the write was in flight is the base
  // the held value is then measured against, so it never shows through.
  const latest = useRef(server);
  latest.current = server;
  // A refetch that brought the row another value replaces what the cell held.
  // Dropped here rather than ignored, so a later return to the old value never
  // revives it.
  const stale = held !== undefined && !busy && !same(server, held.base);
  if (stale) setHeld(undefined);
  const shown = held !== undefined && !stale ? held.value : server;

  const commit = async (optimistic: V, write: (shown: V) => Promise<Settled<V>>): Promise<void> => {
    const previous = shown;
    setRefusal(undefined);
    setHeld({ value: optimistic, base: server });
    setBusy(true);
    let answer: Settled<V>;
    try {
      answer = await write(previous);
    } catch {
      answer = { value: previous, refusal: editor.failedLabel };
    }
    setHeld({ value: answer.value, base: latest.current });
    setRefusal(answer.refusal);
    setBusy(false);
    if (answer.refusal !== undefined) toast.error(answer.refusal);
    else if (editor.committedLabel !== undefined) toast.success(editor.committedLabel(row));
    editor.onSettled(row);
  };

  return { shown, busy, refusal, commit };
}

/** A choice write's answer, read into what the cell shows. */
function settleChoice<V>(outcome: CellWriteOutcome<V>, next: V, previous: V): Settled<V> {
  switch (outcome.status) {
    case "committed":
    case "settled":
      return { value: next };
    case "changed":
      return { value: outcome.current, refusal: outcome.message };
    case "refused":
      return { value: previous, refusal: outcome.message };
  }
}

/** While the list is open: a press outside it, focus moving out of the cell
 * (a Tab out of the list), a scroll anywhere but inside it, or a resize closes
 * it and sends nothing. */
function useDismiss(
  open: boolean,
  frame: RefObject<HTMLSpanElement | null>,
  pop: RefObject<HTMLDivElement | null>,
  dismiss: () => void,
): void {
  const latest = useRef(dismiss);
  latest.current = dismiss;
  useEffect(() => {
    if (!open) return undefined;
    const inside = (target: EventTarget | null, el: HTMLElement | null): boolean =>
      target instanceof Node && el?.contains(target) === true;
    const onPress = (e: Event): void => {
      if (!inside(e.target, frame.current)) latest.current();
    };
    const onScroll = (e: Event): void => {
      if (!inside(e.target, pop.current)) latest.current();
    };
    const onResize = (): void => {
      latest.current();
    };
    // Focus leaving for nowhere (the window losing focus) keeps the list, as
    // a press outside is what closes it then.
    const onFocusOut = (e: FocusEvent): void => {
      if (e.relatedTarget !== null && !inside(e.relatedTarget, frame.current)) latest.current();
    };
    const box = frame.current;
    document.addEventListener("pointerdown", onPress, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    box?.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("pointerdown", onPress, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      box?.removeEventListener("focusout", onFocusOut);
    };
  }, [open, frame, pop]);
}

function EditGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M10.6 2.9l2.5 2.5L6 12.5H3.5V10z" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3.5 8.4l3 3 6-6.6" />
    </svg>
  );
}

/** Static: the busy mark never loops (§1 motion). */
function BusyGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" data-mark="busy">
      <circle cx="4" cy="8" r="1" />
      <circle cx="8" cy="8" r="1" />
      <circle cx="12" cy="8" r="1" />
    </svg>
  );
}

function DangerGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" data-mark="refused">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.8v3.6M8 10.8v.4" />
    </svg>
  );
}

interface FrameProps {
  frameRef: RefObject<HTMLSpanElement | null>;
  buttonRef: RefObject<HTMLButtonElement | null>;
  testId: string;
  value: ReactNode;
  writable: boolean;
  reason: string | undefined;
  busy: boolean;
  busyLabel: string;
  refusal: string | undefined;
  offline: boolean;
  open: boolean;
  editLabel: string;
  popup: "listbox" | "dialog" | undefined;
  pressed: boolean | undefined;
  glyph: ReactNode;
  onPress: () => void;
  children?: ReactNode;
}

/** The cell's box: the value, then the one reserved slot. */
function Frame({
  frameRef,
  buttonRef,
  testId,
  value,
  writable,
  reason,
  busy,
  busyLabel,
  refusal,
  offline,
  open,
  editLabel,
  popup,
  pressed,
  glyph,
  onPress,
  children,
}: FrameProps) {
  const descId = useId();
  // A row that turns read-only under its own commit (a task's Done) unmounts
  // the button that holds the focus. Read before the commit removes it, so the
  // cell itself takes the focus and a keyboard reader keeps their place.
  const focusFell = useRef(false);
  if (!writable && buttonRef.current !== null && buttonRef.current === document.activeElement) {
    focusFell.current = true;
  }
  useLayoutEffect(() => {
    if (!focusFell.current) return;
    focusFell.current = false;
    frameRef.current?.focus();
  }, [writable, frameRef]);
  if (!writable) {
    return (
      <span
        ref={frameRef}
        className="ds-ced is-readonly"
        data-testid={testId}
        data-cell-state="readonly"
        // Focusable from code only, for the focus a commit's own button
        // leaves behind; Tab never stops here.
        tabIndex={-1}
        title={reason}
        aria-describedby={reason !== undefined ? descId : undefined}
      >
        <span className="ds-ced-v">{value}</span>
        <span className="ds-ced-slot" aria-hidden="true" />
        {reason !== undefined ? (
          <span id={descId} hidden>
            {reason}
          </span>
        ) : null}
      </span>
    );
  }
  const description = busy ? busyLabel : refusal;
  const state = busy ? "busy" : open ? "open" : refusal !== undefined ? "refused" : "idle";
  return (
    <span
      ref={frameRef}
      className="ds-ced"
      data-testid={testId}
      data-cell-state={state}
      aria-busy={busy || undefined}
    >
      <span className="ds-ced-v">{value}</span>
      <span className="ds-ced-slot">
        <button
          ref={buttonRef}
          type="button"
          className={refusal !== undefined && !busy ? "ds-ced-btn is-refused" : "ds-ced-btn"}
          aria-label={editLabel}
          aria-describedby={description !== undefined ? descId : undefined}
          title={description}
          // Inert while the write is in flight, and still focusable: a
          // disabled button would drop the focus the commit gave back to it.
          aria-disabled={busy || undefined}
          disabled={offline}
          aria-haspopup={popup}
          aria-expanded={popup !== undefined ? open : undefined}
          aria-pressed={pressed}
          data-testid={`${testId}-edit`}
          onClick={(e) => {
            // The row's own click keeps its meaning, so this one never
            // reaches it.
            e.stopPropagation();
            if (!busy) onPress();
          }}
        >
          {busy ? <BusyGlyph /> : refusal !== undefined ? <DangerGlyph /> : glyph}
        </button>
      </span>
      {description !== undefined ? (
        <span id={descId} hidden>
          {description}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/** The open list's frame: fixed to the viewport, its clicks kept from the
 * row, and Escape heard here so it never reaches a row or a dialog behind. */
function Popover({
  popRef,
  at,
  testId,
  role,
  label,
  onKeyDown,
  children,
}: {
  popRef: RefObject<HTMLDivElement | null>;
  at: Placement;
  testId: string;
  role: "dialog" | undefined;
  label: string | undefined;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  children: ReactNode;
}) {
  return (
    <div
      ref={popRef}
      className={at.up ? "ds-ced-pop ds-ced-pop--up" : "ds-ced-pop"}
      style={placementStyle(at)}
      role={role}
      aria-label={label}
      data-testid={`${testId}-list`}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}

function ChoiceCell<Row>({
  row,
  editor,
}: {
  row: Row;
  editor: SelectCellEditor<Row> | UserCellEditor<Row>;
}) {
  const testId = editor.testId ?? "ds-cell-editor";
  const offline = useOffline();
  const frameRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const focusRef = useRef<HTMLElement | null>(null);
  const listId = useId();
  const { shown, busy, refusal, commit } = useCellWrite<Row, string | null>(
    row,
    editor,
    editor.value(row),
    (a, b) => a === b,
  );
  const [at, setAt] = useState<Placement | undefined>(undefined);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(0);
  // The browser going offline closes the list and sends nothing.
  if (offline && at !== undefined) setAt(undefined);
  const open = at !== undefined;

  useEffect(() => {
    if (open) focusRef.current?.focus();
  }, [open]);

  const close = (refocus: boolean): void => {
    setAt(undefined);
    if (refocus) buttonRef.current?.focus();
  };
  useDismiss(open, frameRef, popRef, () => {
    close(false);
  });

  const options = editor.options(row);
  const needle = filter.trim().toLocaleLowerCase();
  const visible =
    editor.kind === "user" && needle !== ""
      ? options.filter((o) => o.label.toLocaleLowerCase().includes(needle))
      : options;
  const activeIndex = Math.min(active, Math.max(visible.length - 1, 0));
  const activeOption = visible[activeIndex];
  const optionId = (index: number): string => `${listId}-o${String(index)}`;
  const activeId = open && activeOption !== undefined ? optionId(activeIndex) : undefined;

  // The active option stays in sight: the list scrolls inside its own box,
  // since `aria-activedescendant` moves no scroll and a commit must never land
  // on an option the reader could not see.
  useEffect(() => {
    if (activeId === undefined) return;
    const list = document.getElementById(listId);
    const option = document.getElementById(activeId);
    if (list === null || option === null) return;
    const box = list.getBoundingClientRect();
    const at = option.getBoundingClientRect();
    if (at.top < box.top) list.scrollTop -= box.top - at.top;
    else if (at.bottom > box.bottom) list.scrollTop += at.bottom - box.bottom;
  }, [activeId, listId]);

  const choose = (option: CellOption): void => {
    close(true);
    // An unchanged value is no write, and a diverted one is the caller's.
    if (option.value === shown) return;
    if (editor.divert?.(row, option.value) === true) return;
    void commit(option.value, async (previous) =>
      settleChoice(await editor.send(row, option.value, previous), option.value, previous),
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (activeOption !== undefined) choose(activeOption);
    } else if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "PageDown" ||
      e.key === "PageUp"
    ) {
      e.preventDefault();
      const step =
        e.key === "ArrowDown"
          ? 1
          : e.key === "ArrowUp"
            ? -1
            : e.key === "PageDown"
              ? PAGE_STEP
              : -PAGE_STEP;
      setActive(Math.min(Math.max(activeIndex + step, 0), Math.max(visible.length - 1, 0)));
    } else if (e.key === " " && editor.kind === "select") {
      // The page's own scroll would close the list (useDismiss). Space chooses
      // nothing, because its keyup would land on the button the choice gives
      // focus back to and open the list again; Enter commits.
      e.preventDefault();
    } else if (e.key === "Home" || e.key === "End") {
      // The filter box keeps Home and End for its caret.
      if (editor.kind === "user") return;
      e.preventDefault();
      setActive(e.key === "Home" ? 0 : Math.max(visible.length - 1, 0));
    }
  };

  const listbox = (
    <ul
      id={listId}
      role="listbox"
      className="ds-ced-list"
      aria-label={editor.listLabel(row)}
      tabIndex={-1}
      aria-activedescendant={
        editor.kind === "select" && activeOption !== undefined ? optionId(activeIndex) : undefined
      }
      ref={
        editor.kind === "select"
          ? (el) => {
              focusRef.current = el;
            }
          : undefined
      }
    >
      {visible.map((option, index) => (
        <li
          key={option.value}
          id={optionId(index)}
          role="option"
          aria-selected={index === activeIndex}
          className={[
            "ds-ced-opt",
            index === activeIndex ? "is-active" : undefined,
            option.value === shown ? "is-current" : undefined,
          ]
            .filter((x): x is string => x !== undefined)
            .join(" ")}
          onClick={() => {
            choose(option);
          }}
        >
          <span className="ds-ced-tick">{option.value === shown ? <CheckGlyph /> : null}</span>
          {option.label}
        </li>
      ))}
    </ul>
  );

  return (
    <Frame
      frameRef={frameRef}
      buttonRef={buttonRef}
      testId={testId}
      value={editor.display(shown, row)}
      writable={editor.writable(row)}
      reason={editor.reason?.(row)}
      busy={busy}
      busyLabel={editor.busyLabel}
      refusal={refusal}
      offline={offline}
      open={open}
      editLabel={editor.editLabel(row)}
      popup="listbox"
      pressed={undefined}
      glyph={<EditGlyph />}
      onPress={() => {
        if (open) {
          close(true);
          return;
        }
        if (frameRef.current === null) return;
        setFilter("");
        const current = options.findIndex((o) => o.value === shown);
        setActive(current === -1 ? 0 : current);
        setAt(placeBeside(frameRef.current));
      }}
    >
      {at !== undefined ? (
        <Popover
          popRef={popRef}
          at={at}
          testId={testId}
          role={undefined}
          label={undefined}
          onKeyDown={onKeyDown}
        >
          {editor.kind === "user" ? (
            <input
              ref={(el) => {
                focusRef.current = el;
              }}
              type="search"
              className="ds-ced-filter"
              role="combobox"
              aria-label={editor.filterLabel}
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeOption !== undefined ? optionId(activeIndex) : undefined}
              autoComplete="off"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setActive(0);
              }}
            />
          ) : null}
          {listbox}
          {editor.kind === "user" && visible.length === 0 ? (
            <p className="ds-ced-empty">{editor.noMatchLabel}</p>
          ) : null}
        </Popover>
      ) : null}
    </Frame>
  );
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v) => b.includes(v));

function TagsCell<Row>({ row, editor }: { row: Row; editor: TagsCellEditor<Row> }) {
  const testId = editor.testId ?? "ds-cell-editor";
  const offline = useOffline();
  const frameRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const { shown, busy, refusal, commit } = useCellWrite<Row, readonly string[]>(
    row,
    editor,
    editor.value(row),
    sameSet,
  );
  const [at, setAt] = useState<Placement | undefined>(undefined);
  // The set the checklist opened at and the toggles staged over it: nothing is
  // sent while it is open, and apply sends only the tags the reader flipped,
  // so a refetch that lands meanwhile is never read as the reader's change.
  const [checklist, setChecklist] = useState<{
    readonly opened: ReadonlySet<string>;
    readonly staged: ReadonlySet<string>;
  }>({ opened: new Set(), staged: new Set() });
  const { opened, staged } = checklist;
  if (offline && at !== undefined) setAt(undefined);
  const open = at !== undefined;

  useEffect(() => {
    if (open) popRef.current?.querySelector("input")?.focus();
  }, [open]);

  const close = (refocus: boolean): void => {
    setAt(undefined);
    if (refocus) buttonRef.current?.focus();
  };
  useDismiss(open, frameRef, popRef, () => {
    close(false);
  });

  const options = editor.options(row, shown);

  const apply = (): void => {
    close(true);
    const changes = options
      .filter((o) => staged.has(o.value) !== opened.has(o.value))
      .map((o) => ({ option: o, attach: staged.has(o.value) }));
    if (changes.length === 0) return;
    // The set a commit that is wholly accepted ends in, in the order the cell
    // already showed with the attached tags after it.
    const settle = (accepted: (change: (typeof changes)[number]) => boolean): string[] => {
      const keep = shown.filter(
        (v) => !changes.some((c) => c.option.value === v && !c.attach && accepted(c)),
      );
      const add = changes
        .filter((c) => c.attach && accepted(c) && !keep.includes(c.option.value))
        .map((c) => c.option.value);
      return [...keep, ...add];
    };
    void commit(
      settle(() => true),
      async () => {
        // One request per changed tag, each with its own result, so a mixed
        // commit shows the set the server committed.
        const answers = await Promise.all(
          changes.map((c) =>
            editor
              .send(row, { tag: c.option.value, attach: c.attach })
              .catch((): undefined => undefined),
          ),
        );
        const refused: CellTagRefusal[] = [];
        changes.forEach((c, i) => {
          const answer = answers[i];
          if (answer === undefined) {
            refused.push({ ...c, code: undefined, message: editor.failedLabel });
          } else if (answer.status === "refused") {
            refused.push({ ...c, code: answer.code, message: answer.message });
          }
        });
        const isRefused = (c: (typeof changes)[number]): boolean =>
          refused.some((r) => r.option.value === c.option.value);
        return {
          value: settle((c) => !isRefused(c)),
          refusal: refused.length > 0 ? editor.refusedLabel(refused, row) : undefined,
        };
      },
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      apply();
    }
  };

  return (
    <Frame
      frameRef={frameRef}
      buttonRef={buttonRef}
      testId={testId}
      value={editor.display(shown, row)}
      writable={editor.writable(row)}
      reason={editor.reason?.(row)}
      busy={busy}
      busyLabel={editor.busyLabel}
      refusal={refusal}
      offline={offline}
      open={open}
      editLabel={editor.editLabel(row)}
      popup="dialog"
      pressed={undefined}
      glyph={<EditGlyph />}
      onPress={() => {
        if (open) {
          close(true);
          return;
        }
        if (frameRef.current === null) return;
        setChecklist({ opened: new Set(shown), staged: new Set(shown) });
        setAt(placeBeside(frameRef.current));
      }}
    >
      {at !== undefined ? (
        <Popover
          popRef={popRef}
          at={at}
          testId={testId}
          role="dialog"
          label={editor.listLabel(row)}
          onKeyDown={onKeyDown}
        >
          <div className="ds-ced-checks" role="group" aria-label={editor.listLabel(row)}>
            {options.map((option) => (
              <label key={option.value} className="ds-ced-check">
                <input
                  type="checkbox"
                  checked={staged.has(option.value)}
                  onChange={() => {
                    const next = new Set(staged);
                    if (next.has(option.value)) next.delete(option.value);
                    else next.add(option.value);
                    setChecklist({ opened, staged: next });
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <div className="ds-ced-foot">
            <Button size="sm" onClick={apply} data-testid={`${testId}-apply`}>
              {editor.applyLabel}
            </Button>
          </div>
        </Popover>
      ) : null}
    </Frame>
  );
}

function ToggleCell<Row>({ row, editor }: { row: Row; editor: ToggleCellEditor<Row> }) {
  const testId = editor.testId ?? "ds-cell-editor";
  const offline = useOffline();
  const frameRef = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { shown, busy, refusal, commit } = useCellWrite<Row, boolean>(
    row,
    editor,
    editor.value(row),
    (a, b) => a === b,
  );
  return (
    <Frame
      frameRef={frameRef}
      buttonRef={buttonRef}
      testId={testId}
      value={editor.display(shown, row)}
      writable={editor.writable(row)}
      reason={editor.reason?.(row)}
      busy={busy}
      busyLabel={editor.busyLabel}
      refusal={refusal}
      offline={offline}
      open={false}
      editLabel={editor.editLabel(row)}
      popup={undefined}
      pressed={shown}
      glyph={<CheckGlyph />}
      onPress={() => {
        // The press is the commit.
        const next = !shown;
        void commit(next, async (previous) =>
          settleChoice(await editor.send(row, next), next, previous),
        );
      }}
    />
  );
}

/** One editable cell (design-system §3.13). `DataTable` renders it for a
 * column that passes `editable`, from 769 px up. */
export function CellEditor<Row>({ row, editor }: { row: Row; editor: CellEditable<Row> }) {
  switch (editor.kind) {
    case "select":
    case "user":
      return <ChoiceCell row={row} editor={editor} />;
    case "tags":
      return <TagsCell row={row} editor={editor} />;
    case "toggle":
      return <ToggleCell row={row} editor={editor} />;
  }
}
